import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import StopIcon from "@mui/icons-material/Stop";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useSnackbar } from "notistack";
import { useDispatch } from "src/redux/store";
import axios from "src/utils/axios";
import { GetAllEmployees, getCurrentClockingSuccess } from "src/redux/slices/employee";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import duration from "dayjs/plugin/duration";
import "dayjs/locale/es";
import { buildShiftAction } from "src/utils/clocking/clockingDevice";

dayjs.extend(utc);
dayjs.extend(duration);
dayjs.locale("es");

const fromUtc = (d) => (d ? dayjs.utc(d).local() : null);

/**
 * Panel que se muestra cuando el empleado tiene MÁS DE UN fichaje sin
 * finalizar. Lista cada uno con:
 *   - fecha del turno + hora de entrada
 *   - duración transcurrida
 *   - botón "Finalizar ahora" (POST /employeeShift STOP_SHIFT sin hora)
 *   - botón "Ajustar hora" → despliega input datetime-local para elegir
 *     el checkOut y envía manualCheckOutTime.
 *
 * Tras cada cierre exitoso hace GetAllEmployees para refrescar el
 * conjunto de shifts abiertos. Cuando queda 1 solo o 0, el modal padre
 * vuelve al comportamiento normal (timer + botón start/stop).
 */
export default function PendingShiftsPanel({
  employee,
  openShifts,
  onAllClosed,
  onClose,
  onSkip,
}) {
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [expandedId, setExpandedId] = useState(null); // shift.id con selector abierto
  const [manualValue, setManualValue] = useState(""); // datetime-local string
  const [loadingId, setLoadingId] = useState(null);

  // Lista LOCAL de pendientes — inicializada desde la prop `openShifts`,
  // pero actualizada optimísticamente al cerrar cada shift. Sin esto
  // había una ventana entre "POST cierre OK" y "GetAllEmployees refresca
  // el store" en la que el panel seguía renderizando el shift ya
  // cerrado. Al reabrir el modal se veía como "actual en curso" y era
  // profundamente confuso. Ahora al cerrar quitamos localmente + hacemos
  // el refresh en background.
  const [localList, setLocalList] = useState(openShifts);
  useEffect(() => {
    setLocalList(openShifts);
  }, [openShifts]);

  const closeShift = async (item, manualIso = null) => {
    setLoadingId(item.shift.id);
    try {
      await axios.post("employeeShift", buildShiftAction({
        employeeId: employee.id,
        workScheduleItemId: item.workItem.id,
        type: 1, // STOP_SHIFT
        shiftId: item.shift.id,
        manualCheckOutTime: manualIso,
      }));
      enqueueSnackbar(
        manualIso ? "Fichaje cerrado con la hora indicada" : "Fichaje cerrado",
        { variant: "success" },
      );
      setExpandedId(null);
      setManualValue("");

      // OPTIMISTIC: eliminar el shift de la lista local antes de que
      // llegue el refresh del store. Si queda vacío, cerramos el modal
      // desde aquí (no dependemos del re-render del padre).
      const remaining = localList.filter((o) => o.shift.id !== item.shift.id);
      setLocalList(remaining);

      // Refresh en background + reselect del currentClocking para que
      // el store recompute el foco natural sobre los datos nuevos. Sin
      // el reselect, `state.currentClocking` seguía apuntando al shift
      // que acabamos de cerrar y al reabrir el modal mostraba ese
      // fichaje fantasma como "actual en curso".
      // Sin id: GetAllEmployees(id) también cambia el empleado activo de la
      // tablet (setEmployee); cerrar un fichaje no es cambiar de empleado.
      Promise.resolve(dispatch(GetAllEmployees())).then(() => {
        dispatch(getCurrentClockingSuccess(employee.id));
      });

      if (remaining.length === 0) {
        onAllClosed?.();
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400) {
        enqueueSnackbar(
          "La hora indicada no es válida (revisa que no sea futura ni anterior a la entrada).",
          { variant: "error" },
        );
      } else {
        enqueueSnackbar("Error al cerrar el fichaje", { variant: "error" });
      }
    } finally {
      setLoadingId(null);
    }
  };

  const closeShiftScheduled = (item) => {
    // "Finalizar a la hora prevista" — usa WorkItem.End como CheckOut.
    // Sólo aplica si el turno tiene un fin programado y ya pasó.
    const scheduledEnd = fromUtc(item.workItem.end);
    if (!scheduledEnd) return;
    const now = dayjs();
    // Si aún no ha llegado la hora fin, no permitimos usar futuro.
    const effective = scheduledEnd.isAfter(now) ? now : scheduledEnd;
    closeShift(item, effective.utc().toISOString());
  };

  const toggleExpand = (item) => {
    if (expandedId === item.shift.id) {
      setExpandedId(null);
      setManualValue("");
      return;
    }
    // Prefill del datetime-local con la hora fin programada del turno si
    // existe, en su defecto la hora actual. Ahorra clicks al empleado que
    // "olvidó fichar a la hora de cierre normal".
    const scheduledEnd = fromUtc(item.workItem.end);
    const now = dayjs();
    const prefill = scheduledEnd && scheduledEnd.isBefore(now)
      ? scheduledEnd
      : now;
    // Formato requerido por <input type="datetime-local">: YYYY-MM-DDTHH:mm.
    setManualValue(prefill.format("YYYY-MM-DDTHH:mm"));
    setExpandedId(item.shift.id);
  };

  const handleManualSubmit = (item) => {
    if (!manualValue) return;
    // El datetime-local es hora LOCAL sin timezone. Convertimos a ISO UTC
    // porque el backend espera `DateTime?` y lo compara con `DateTime.UtcNow`.
    const iso = dayjs(manualValue).utc().toISOString();
    closeShift(item, iso);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 480 }}>
      {/* Header */}
      <Box
        sx={{
          px: 3, py: 2,
          borderBottom: "1px solid rgba(99,102,241,0.1)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "linear-gradient(90deg,#fef3c7 0%,#fef9c3 100%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar sx={{ bgcolor: "#d97706", width: 40, height: 40 }}>
            <AccessTimeIcon />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={800}>
              {employee?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {localList.length === 1
                ? "Tienes 1 fichaje sin finalizar"
                : `Tienes ${localList.length} fichajes sin finalizar`}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {typeof onSkip === "function" && (
            <Button
              onClick={onSkip}
              size="small"
              variant="contained"
              color="primary"
              sx={{
                borderRadius: 2, fontWeight: 700, fontSize: 12,
                textTransform: "none", boxShadow: "0 2px 8px rgba(99,102,241,0.3)",
              }}
            >
              Fichar mi turno de hoy
            </Button>
          )}
          <Button
            onClick={onClose}
            size="small"
            sx={{
              border: "1.5px solid rgba(99,102,241,0.2)", borderRadius: 2,
              fontWeight: 600, fontSize: 12, color: "text.secondary",
              textTransform: "none",
            }}
          >
            Cerrar
          </Button>
        </Box>
      </Box>

      {/* Aviso */}
      <Alert
        severity="warning"
        icon={<AccessTimeIcon />}
        sx={{ borderRadius: 0 }}
      >
        Finaliza cada fichaje con la hora real de salida.
        <strong> A la hora prevista</strong> lo cierra a la hora fin del
        turno programado; <strong>Ajustar</strong> te deja elegir cualquier
        hora; <strong>Ahora</strong> pone la hora actual. Puedes
        posponerlos y fichar tu turno de hoy con el botón de arriba.
      </Alert>

      {/* Lista */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>
        <Stack spacing={1.5}>
          {localList.map((item) => {
            const checkIn = fromUtc(item.shift.checkInTime);
            const scheduledStart = fromUtc(item.workItem.start);
            const scheduledEnd = fromUtc(item.workItem.end);
            const dur = checkIn ? dayjs.duration(dayjs().diff(checkIn)) : null;
            const hours = dur ? Math.floor(dur.asHours()) : 0;
            const mins = dur ? dur.minutes() : 0;
            const expanded = expandedId === item.shift.id;
            const busy = loadingId === item.shift.id;
            // "A la hora prevista" solo tiene sentido si el turno tiene
            // hora fin programada Y esa hora es posterior al CheckIn
            // (evita CheckOut < CheckIn que el backend rechazaría). Si
            // el turno programado aún no ha terminado, el helper cierra
            // con "ahora" en su lugar, así el botón sigue siendo útil.
            const canUseScheduled =
              scheduledEnd &&
              checkIn &&
              scheduledEnd.isAfter(checkIn);

            return (
              <Box
                key={item.shift.id}
                sx={{
                  borderRadius: 2,
                  border: "1px solid rgba(239,68,68,0.2)",
                  bgcolor: "rgba(254,242,242,0.5)",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} sx={{ textTransform: "capitalize" }}>
                        {scheduledStart?.format("dddd D MMMM")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Turno programado {scheduledStart?.format("HH:mm")}
                        {scheduledEnd && ` – ${scheduledEnd.format("HH:mm")}`}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                        <ScheduleIcon sx={{ fontSize: 14, color: "error.main" }} />
                        <Typography variant="caption" fontWeight={700} color="error.main">
                          Entrada: {checkIn?.format("HH:mm")} · {hours}h {mins}m abierto
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      size="small"
                      color="error"
                      label="Pendiente"
                      sx={{ fontWeight: 700, fontSize: 10 }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: canUseScheduled ? "1fr 1fr 1fr" : "1fr 1fr",
                      gap: 1,
                      mt: 1.5,
                    }}
                  >
                    {canUseScheduled && (
                      <LoadingButton
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<CheckCircleOutlineIcon />}
                        loading={busy && !expanded}
                        onClick={() => closeShiftScheduled(item)}
                        sx={{ fontWeight: 700, textTransform: "none" }}
                      >
                        A la hora prevista ({scheduledEnd.format("HH:mm")})
                      </LoadingButton>
                    )}
                    <LoadingButton
                      variant="contained"
                      color="error"
                      size="small"
                      startIcon={<StopIcon />}
                      loading={busy && !expanded}
                      onClick={() => closeShift(item)}
                      sx={{ fontWeight: 700, textTransform: "none" }}
                    >
                      Ahora
                    </LoadingButton>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => toggleExpand(item)}
                      endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      sx={{ fontWeight: 600, textTransform: "none" }}
                    >
                      Ajustar
                    </Button>
                  </Box>
                </Box>

                <Collapse in={expanded}>
                  <Divider />
                  <Box sx={{ px: 2, py: 1.5, bgcolor: "rgba(255,255,255,0.6)" }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Selecciona la hora real de salida:
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <TextField
                        type="datetime-local"
                        size="small"
                        value={manualValue}
                        onChange={(e) => setManualValue(e.target.value)}
                        // No permitimos futuro. `checkIn` como mínimo evita
                        // duración negativa.
                        inputProps={{
                          max: dayjs().format("YYYY-MM-DDTHH:mm"),
                          min: checkIn?.format("YYYY-MM-DDTHH:mm"),
                        }}
                        fullWidth
                      />
                      <LoadingButton
                        variant="contained"
                        color="success"
                        size="small"
                        loading={busy}
                        startIcon={<CheckCircleOutlineIcon />}
                        onClick={() => handleManualSubmit(item)}
                        disabled={!manualValue}
                        sx={{ fontWeight: 700, textTransform: "none" }}
                      >
                        Cerrar
                      </LoadingButton>
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
}
